import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { transactionService } from '@/services/transactionService';
import { toast } from 'react-hot-toast';


// Interface for scheduled report from API
interface ScheduledReportItem {
  schedule_id: string;
  report_id: number;
  report_name: string;
  report_type: string;
  format: string;
  date_range: {
    start: string;
    end: string;
  };
  frequency: 'daily' | 'weekly' | 'monthly';
  day: number | null;
  date: number | null;
  time: string;
  next_run: string;
  created_at: string;
}

// API Response interface
interface ScheduledReportsResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: ScheduledReportItem[];
}

interface ScheduledReportsListProps {
  reportType?: string;
}

export const ScheduledReportsList: React.FC<ScheduledReportsListProps> = ({
  reportType = 'payments',
}) => {
  // State
  const [reports, setReports] = useState<ScheduledReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<ScheduledReportItem | null>(null);

  // Load scheduled reports
  const loadScheduledReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const currentPage = page + 1; // Convert 0-based to 1-based page
      const response = await transactionService.getScheduledReportsPaginated(
        reportType,
        currentPage,
        rowsPerPage
      );
      
      setReports(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      console.error('Failed to load scheduled reports:', err);
      toast.error('Failed to load scheduled reports');
      setReports([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [reportType, page, rowsPerPage]);

  // Load reports on mount and when pagination changes
  useEffect(() => {
    loadScheduledReports();
  }, [loadScheduledReports]);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Filter reports based on search term (client-side filtering)
  const filteredReports = reports.filter((report) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      report.report_name.toLowerCase().includes(searchLower) ||
      report.format.toLowerCase().includes(searchLower) ||
      report.frequency.toLowerCase().includes(searchLower)
    );
  });

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (report: ScheduledReportItem) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  // Handle delete report
  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    setDeletingId(reportToDelete.schedule_id);
    handleCloseDeleteDialog();

    try {
      await transactionService.deleteScheduledReport(reportToDelete.schedule_id, reportType);
      toast.success('Scheduled report deleted successfully');
      // Reload the list
      await loadScheduledReports();
    } catch (err) {
      console.error('Failed to delete scheduled report:', err);
      toast.error('Failed to delete scheduled report');
    } finally {
      setDeletingId(null);
    }
  };

  // Format time for display (12-hour format)
  const formatTimeDisplay = (time: string) => {
    if (!time) return '-';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Format date for display
  const formatDateDisplay = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get frequency display text
  const getFrequencyDisplay = (report: ScheduledReportItem) => {
    switch (report.frequency) {
      case 'daily':
        return `Daily at ${formatTimeDisplay(report.time)}`;
      case 'weekly':
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayIndex = report.day !== null ? report.day : 0;
        return `Every ${days[dayIndex]} at ${formatTimeDisplay(report.time)}`;
      case 'monthly':
        const dateNum = report.date || 1;
        return `Monthly on day ${dateNum} at ${formatTimeDisplay(report.time)}`;
      default:
        return report.frequency;
    }
  };

  // Get frequency chip color
  const getFrequencyColor = (frequency: string): 'primary' | 'secondary' | 'success' => {
    switch (frequency) {
      case 'daily':
        return 'primary';
      case 'weekly':
        return 'secondary';
      case 'monthly':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with Search and Refresh */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3e4fe0' }}>
          <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Scheduled Reports
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search Field */}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                background: 'white',
              },
            }}
          />

          {/* Refresh Button */}
          <Tooltip title="Refresh">
            <IconButton
              onClick={loadScheduledReports}
              disabled={isLoading}
              sx={{
                color: '#3e4fe0',
                '&:hover': { backgroundColor: 'rgba(62, 79, 224, 0.08)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Report Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Format
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Frequency
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Next Run
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Created at
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2, color: '#3e4fe0' }} />
                      <Typography variant="body1" color="textSecondary">
                        Loading scheduled reports...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm
                        ? 'No scheduled reports found matching your search.'
                        : 'No scheduled reports found. Create one from the Export drawer.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report) => (
                  <TableRow key={report.schedule_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {report.report_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.format.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(62, 79, 224, 0.1)',
                          color: '#3e4fe0',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={report.frequency.charAt(0).toUpperCase() + report.frequency.slice(1)}
                          size="small"
                          color={getFrequencyColor(report.frequency)}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {getFrequencyDisplay(report)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateDisplay(report.next_run)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDateDisplay(report.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete report">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(report)}
                          disabled={deletingId === report.schedule_id}
                          sx={{
                            color: '#e53935',
                            '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.08)' },
                          }}
                        >
                          {deletingId === report.schedule_id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={searchTerm ? filteredReports.length : totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Scheduled Report</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the scheduled report{' '}
            <strong>"{reportToDelete?.report_name}"</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteReport}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: '#4caf50',
            color: '#fff',
            '& .MuiAlert-icon': { color: '#fff' },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduledReportsList;

